import { useEffect, useState } from 'react'
import { useAdmin } from '@/hooks/useAdmin'
import type {
  ServicesStatus,
  HealthRun,
  HealthHistoryRow,
  AreaResult,
  TestStatus,
} from '@/hooks/useAdmin'
import { Button } from '@/components/ui/button'
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  MinusCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Play,
  Clock,
  Database,
  Zap,
  Mail,
  CreditCard,
  GitCompare,
  Camera,
  ScrollText,
  Shield,
  FlaskConical,
  Settings,
  Lock,
  Cloud,
} from 'lucide-react'

// ── Icon map for area names ────────────────────────────────────────────────────
const AREA_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Connections: Cloud,
  'Metadata Compare': GitCompare,
  'Data Compare': Database,
  Snapshots: Camera,
  'Org Status': Activity,
  'Audit Trail': ScrollText,
  'Email Logs': Mail,
  Permissions: Shield,
  'Sandbox Seeder': FlaskConical,
  'Auth & Settings': Settings,
  Billing: CreditCard,
  Infrastructure: Zap,
  'Salesforce (live)': Cloud,
}

// ── StatusBadge ───────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status?: TestStatus | string }) {
  if (!status) return null
  const map: Record<string, { Icon: React.ComponentType<{ className?: string }>; cls: string; label: string }> = {
    pass: { Icon: CheckCircle2, cls: 'status-pill-success', label: 'PASS' },
    warn: { Icon: AlertTriangle, cls: 'status-pill-amber', label: 'WARN' },
    fail: { Icon: XCircle, cls: 'status-pill-destructive', label: 'FAIL' },
    skip: { Icon: MinusCircle, cls: 'status-pill-muted', label: 'SKIP' },
    running: { Icon: Loader2, cls: 'status-pill-info', label: 'RUNNING' },
  }
  const e = map[status] ?? map.skip
  const Icon = e.Icon
  return (
    <span className={`status-pill inline-flex items-center gap-1 ${e.cls}`}>
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} /> {e.label}
    </span>
  )
}

// ── ServiceChip ───────────────────────────────────────────────────────────────
function ServiceChip({ ok, label, warnIfOff }: { ok: boolean; label: string; warnIfOff?: boolean }) {
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs border"
      style={{
        background: ok
          ? 'hsl(var(--success) / 0.1)'
          : warnIfOff
            ? 'hsl(var(--amber) / 0.1)'
            : 'hsl(var(--muted) / 0.6)',
        borderColor: ok
          ? 'hsl(var(--success) / 0.3)'
          : warnIfOff
            ? 'hsl(var(--amber) / 0.3)'
            : 'hsl(var(--border))',
        color: ok
          ? 'hsl(var(--success))'
          : warnIfOff
            ? 'hsl(var(--amber))'
            : 'hsl(var(--muted-foreground))',
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{
          background: ok
            ? 'hsl(var(--success))'
            : warnIfOff
              ? 'hsl(var(--amber))'
              : 'hsl(var(--muted-foreground))',
        }}
      />
      {label}
    </div>
  )
}

// ── AreaCard ─────────────────────────────────────────────────────────────────
function AreaCard({ area }: { area: AreaResult }) {
  const [open, setOpen] = useState(area.status === 'fail' || area.status === 'warn')
  const Icon = AREA_ICONS[area.area] ?? Activity
  const counts = {
    pass: area.tests.filter((t) => t.status === 'pass').length,
    warn: area.tests.filter((t) => t.status === 'warn').length,
    fail: area.tests.filter((t) => t.status === 'fail').length,
    skip: area.tests.filter((t) => t.status === 'skip').length,
  }
  return (
    <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 hover:bg-accent/30 transition-colors text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground">{area.area}</span>
            <StatusBadge status={area.status} />
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 font-mono">
            {counts.pass} pass · {counts.warn} warn · {counts.fail} fail · {counts.skip} skip ·{' '}
            {area.durationMs}ms
          </div>
        </div>
      </button>
      {open && (
        <div className="border-t border-border bg-background/40">
          {area.tests.map((t, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 border-b border-border/60 last:border-b-0"
            >
              <StatusBadge status={t.status} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-foreground">{t.name}</div>
                {t.message && (
                  <div className="text-xs text-muted-foreground mt-0.5">{t.message}</div>
                )}
                {t.details != null && (
                  <pre className="text-[10px] text-muted-foreground mt-1 font-mono bg-muted/40 rounded p-1.5 overflow-x-auto">
                    {JSON.stringify(t.details, null, 2)}
                  </pre>
                )}
              </div>
              <div className="text-[10px] text-muted-foreground font-mono shrink-0 mt-1">
                {t.durationMs}ms
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── HistorySparkline ──────────────────────────────────────────────────────────
function HistorySparkline({ runs }: { runs: HealthHistoryRow[] }) {
  if (runs.length === 0) return null
  const W = 280
  const H = 32
  const reversed = [...runs].reverse()
  const points = reversed.map((r, i) => {
    const total = r.summary?.totalTests || 1
    const pct = ((r.summary?.pass || 0) / total) * 100
    const x = (i / Math.max(1, reversed.length - 1)) * W
    const y = H - (pct / 100) * H
    return { x, y, pct, status: r.status }
  })
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ')
  return (
    <svg width={W} height={H} className="overflow-visible">
      <path d={path} fill="none" stroke="hsl(var(--primary) / 0.6)" strokeWidth="1.5" />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="2.5"
          fill={
            p.status === 'fail'
              ? 'hsl(var(--destructive))'
              : p.status === 'warn'
                ? 'hsl(var(--amber))'
                : 'hsl(var(--success))'
          }
        />
      ))}
    </svg>
  )
}

// ── SystemPage ────────────────────────────────────────────────────────────────
export function SystemPage() {
  const { getHealthStatus, runHealth, listHealthRuns, getHealthRun } = useAdmin()

  const [services, setServices] = useState<ServicesStatus | null>(null)
  const [run, setRun] = useState<HealthRun | null>(null)
  const [history, setHistory] = useState<HealthHistoryRow[]>([])
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')

  const loadStatus = async () => {
    try {
      const r = await getHealthStatus()
      setServices(r.services)
    } catch {
      setError('Failed to load service status')
    }
  }

  const loadHistory = async (currentRun?: HealthRun | null) => {
    try {
      const r = await listHealthRuns(10)
      setHistory(r.runs)
      const mostRecentFinished = r.runs.find((x) => x.finished_at && x.status !== 'running')
      if (mostRecentFinished && !currentRun) {
        const detail = await getHealthRun(mostRecentFinished.id)
        const rawRun = detail.run
        setRun({
          runId: rawRun.id,
          startedAt: rawRun.started_at,
          finishedAt: rawRun.finished_at ?? undefined,
          durationMs: rawRun.duration_ms ?? undefined,
          status: rawRun.status as TestStatus,
          summary: rawRun.summary ?? undefined,
          areas: rawRun.areas,
        })
      }
    } catch {
      // non-critical
    }
  }

  useEffect(() => {
    loadStatus()
    loadHistory(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRunHealth = async () => {
    setRunning(true)
    setError('')
    try {
      const r = await runHealth()
      setRun(r.run)
      loadHistory(r.run)
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? 'Run failed')
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="brand-wordmark text-2xl flex items-center gap-2">
            <Activity className="h-6 w-6" /> System Health
          </h1>
          <p className="kicker text-muted-foreground mt-1">
            Regression-style verification of every feature area. Synthetic, transactional, no destructive writes.
          </p>
        </div>
        <Button onClick={handleRunHealth} disabled={running}>
          {running ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Running…
            </>
          ) : (
            <>
              <Play className="h-4 w-4" /> Run all checks
            </>
          )}
        </Button>
      </div>

      {/* Service initialization banner */}
      {services && (
        <div className="rounded-xl border border-border bg-card/30 p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
            Service initialization
          </div>
          <div className="flex flex-wrap gap-2">
            <ServiceChip ok={services.database} label="Postgres" warnIfOff />
            <ServiceChip ok={services.redis} label="Redis" warnIfOff />
            <ServiceChip ok={services.stripe} label="Stripe" />
            <ServiceChip ok={services.stripeWebhook} label="Stripe webhook" />
            <ServiceChip ok={services.email} label="Resend (email)" />
            <ServiceChip ok={services.anthropic} label="Anthropic AI" />
          </div>
          <div className="text-[11px] text-muted-foreground mt-2.5">
            Green = configured. Muted = optional service unset (feature degrades gracefully). Amber =
            configured but reachable check failed.
          </div>
        </div>
      )}

      {error && (
        <div
          className="rounded-lg p-3 text-sm border flex items-center gap-2"
          style={{
            background: 'hsl(var(--destructive) / 0.1)',
            borderColor: 'hsl(var(--destructive) / 0.2)',
            color: 'hsl(var(--destructive))',
          }}
        >
          <XCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Run summary */}
      {run?.summary && (
        <div className="rounded-xl border border-border bg-card/50 p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  Last run
                </span>
                <StatusBadge status={run.status} />
              </div>
              <div className="text-2xl font-semibold mt-1 text-foreground">
                {run.summary.pass}{' '}
                <span className="text-muted-foreground text-base">
                  of {run.summary.totalTests} tests passed
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                {run.startedAt && (
                  <span>
                    <Clock className="h-3 w-3 inline mr-1" />
                    {new Date(run.startedAt).toLocaleString()}
                  </span>
                )}
                <span>{run.durationMs}ms total</span>
                <span>{run.summary.totalAreas} areas</span>
              </div>
              {(run.summary.warn > 0 || run.summary.fail > 0) && (
                <div className="text-xs mt-2 flex items-center gap-3">
                  {run.summary.warn > 0 && (
                    <span style={{ color: 'hsl(var(--amber))' }}>
                      ⚠ {run.summary.warn} warning{run.summary.warn === 1 ? '' : 's'}
                    </span>
                  )}
                  {run.summary.fail > 0 && (
                    <span style={{ color: 'hsl(var(--destructive))' }}>
                      ✗ {run.summary.fail} failure{run.summary.fail === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              )}
            </div>
            {history.length > 1 && (
              <div className="flex flex-col items-end gap-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Pass-rate trend
                </div>
                <HistorySparkline runs={history} />
                <div className="text-[10px] text-muted-foreground font-mono">
                  last {history.length} runs
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Per-area cards */}
      {run?.areas && run.areas.length > 0 && (
        <div className="space-y-3">
          {run.areas.map((a, i) => (
            <AreaCard key={i} area={a} />
          ))}
        </div>
      )}

      {/* Run history table */}
      {history.length > 0 && (
        <div className="rounded-xl border border-border bg-card/30 overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Run history</span>
            <span className="text-xs text-muted-foreground">last {history.length}</span>
          </div>
          <div className="divide-y divide-border">
            {history.map((r) => {
              const s = r.summary
              return (
                <div key={r.id} className="px-5 py-3 flex items-center gap-3 text-sm">
                  <StatusBadge status={r.status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-foreground/85">
                      {new Date(r.started_at).toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {s?.pass ?? '·'} pass · {s?.warn ?? '·'} warn ·{' '}
                      {s?.fail ?? '·'} fail · {r.duration_ms ?? '?'}ms · by{' '}
                      {r.triggered_by_email}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {!run && !running && (
        <div className="rounded-xl border border-dashed border-border bg-card/20 p-10 text-center">
          <Lock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <div className="text-foreground font-medium">No checks run yet</div>
          <div className="text-sm text-muted-foreground mt-1">
            Click "Run all checks" to verify every feature area.
          </div>
        </div>
      )}
    </div>
  )
}
